import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "border border-destructive bg-transparent text-destructive-text hover:bg-destructive/10",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-hb-blue-fill text-white shadow-card hover:bg-hb-blue-fill/90 hover:shadow-card-hover",
        tertiary: "bg-muted text-foreground hover:bg-muted/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-lit underline-offset-4 hover:underline",
        // The main button. Its look, a purple gradient in light and a white button
        // with a glow in Dusk, comes from the --cta tokens in index.css, so this
        // class list names no colour and no theme.
        cta: "border-cta-w border-cta-edge bg-cta bg-cta-image text-cta-foreground shadow-cta hover:[filter:var(--cta-hover)] active:[filter:var(--cta-active)]",
      },
      size: {
        // 44px below `sm`: the smallest target a thumb hits reliably (Apple
        // HIG, WCAG 2.5.5). From `sm` up a pointer is likely and the denser
        // desktop sizes stay.
        default: "h-10 px-5 py-2 max-sm:h-11",
        sm: "h-9 rounded-md px-3 max-sm:h-11",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10 max-sm:size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
